/**
 * Type definitions cho API client system
 */

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface ApiOptions {
  /** Base URL cho API service */
  baseUrl: string | (() => string);
  /** Default headers cho mọi request */
  defaultHeaders?: Record<string, string>;
  /** Function để lấy auth token */
  getAuthToken?: () =>
    string | undefined | null | Promise<string | undefined | null>;
  /** Timeout mặc định (ms), 0 = không timeout */
  defaultTimeoutMs?: number;
  /** Callback khi gặp 401 Unauthorized */
  onUnauthorized?: () => void | Promise<void>;
  /** Function để refresh token, trả về true nếu thành công */
  refreshToken?: () => Promise<boolean>;
  /** Tên service để logging */
  name?: string;
  /** Fetch credentials mode - 'omit' để tránh sec-fetch-storage-access header */
  credentials?: RequestCredentials;
}

export interface RequestOptions<TBody = unknown> {
  /** HTTP method */
  method?: HttpMethod;
  /** API path (relative to baseUrl) */
  path: string;
  /** Query parameters */
  query?: Record<string, string | number | boolean | undefined | null>;
  /** Request body */
  body?: TBody;
  /** Additional headers */
  headers?: Record<string, string>;
  /** Timeout cho request này (ms), 0 = không timeout */
  timeoutMs?: number;
  /** AbortSignal để cancel request */
  signal?: AbortSignal;
  /** Response type parsing */
  responseType?: 'auto' | 'json' | 'text' | 'blob' | 'arrayBuffer';
  /** Skip authentication - không inject token vào request */
  skipAuth?: boolean;
}

export class ApiError<T = unknown> extends Error {
  status: number;
  statusText: string;
  body: T | null;
  url: string;

  constructor(params: {
    message: string;
    status: number;
    statusText: string;
    body: T | null;
    url: string;
  }) {
    super(params.message);
    this.name = 'ApiError';
    this.status = params.status;
    this.statusText = params.statusText;
    this.body = params.body;
    this.url = params.url;
  }
}

export interface UseGetOptions {
  /** Tự động fetch khi component mount */
  enabled?: boolean;
  /** Refetch interval (ms) */
  refetchInterval?: number;
  /** Refetch khi window focus */
  refetchOnWindowFocus?: boolean;
}

export interface UseGetResult<TData = unknown> {
  /** Response data */
  data: TData | undefined;
  /** Loading state */
  isLoading: boolean;
  /** Error object */
  error: ApiError | null;
  /** Refetch function */
  refetch: () => Promise<void>;
}

export interface UseMutationOptions<TData = unknown, TVariables = unknown> {
  /** Callback khi mutation thành công */
  onSuccess?: (data: TData, variables: TVariables) => void | Promise<void>;
  /** Callback khi mutation thất bại */
  onError?: (error: ApiError, variables: TVariables) => void | Promise<void>;
}

export interface UseMutationResult<TData = unknown, TVariables = unknown> {
  /** Execute mutation */
  mutate: (variables: TVariables) => Promise<TData | undefined>;
  /** Execute mutation với options override */
  mutateAsync: (
    variables: TVariables,
    options?: UseMutationOptions<TData, TVariables>
  ) => Promise<TData | undefined>;
  /** Loading state */
  isLoading: boolean;
  /** Error object */
  error: ApiError | null;
  /** Reset mutation state */
  reset: () => void;
}

export interface ApiInstance {
  /** Configured service URL for transports such as EventSource. */
  readonly baseUrl: string;
  /** GET request */
  get<TResponse = unknown>(
    path: string,
    query?: RequestOptions['query'],
    options?: Omit<RequestOptions, 'path' | 'method' | 'query'>
  ): Promise<TResponse>;
  /** POST request */
  post<TResponse = unknown, TBody = unknown>(
    path: string,
    body?: TBody,
    options?: Omit<RequestOptions<TBody>, 'path' | 'method' | 'body'>
  ): Promise<TResponse>;
  /** PUT request */
  put<TResponse = unknown, TBody = unknown>(
    path: string,
    body?: TBody,
    options?: Omit<RequestOptions<TBody>, 'path' | 'method' | 'body'>
  ): Promise<TResponse>;
  /** PATCH request */
  patch<TResponse = unknown, TBody = unknown>(
    path: string,
    body?: TBody,
    options?: Omit<RequestOptions<TBody>, 'path' | 'method' | 'body'>
  ): Promise<TResponse>;
  /** DELETE request */
  delete<TResponse = unknown, TBody = unknown>(
    path: string,
    body?: TBody,
    options?: Omit<RequestOptions<TBody>, 'path' | 'method' | 'body'>
  ): Promise<TResponse>;
  /** Generic request method */
  request<TResponse = unknown, TBody = unknown>(
    options: RequestOptions<TBody>
  ): Promise<TResponse>;
  /** React hook cho GET requests */
  useGet<TData = unknown>(
    path: string,
    query?: RequestOptions['query'],
    options?: UseGetOptions
  ): UseGetResult<TData>;
  /** React hook cho POST requests */
  usePost<TData = unknown, TVariables = unknown>(
    path: string,
    options?: UseMutationOptions<TData, TVariables>
  ): UseMutationResult<TData, TVariables>;
  /** React hook cho PUT requests */
  usePut<TData = unknown, TVariables = unknown>(
    path: string,
    options?: UseMutationOptions<TData, TVariables>
  ): UseMutationResult<TData, TVariables>;
  /** React hook cho PATCH requests */
  usePatch<TData = unknown, TVariables = unknown>(
    path: string,
    options?: UseMutationOptions<TData, TVariables>
  ): UseMutationResult<TData, TVariables>;
  /** React hook cho DELETE requests */
  useDelete<TData = unknown, TVariables = unknown>(
    path: string,
    options?: UseMutationOptions<TData, TVariables>
  ): UseMutationResult<TData, TVariables>;
}
